import { api } from './api';

export type Resort = {
  id: number;
  title: string;
  domains: Array<{ id: number; name: string }>;
  priority_domain?: { id: number; name: string };
  country_id: number;
  country?: {
    id: number;
    name: string;
    code: string | null;
  } | null;
  lat: number;
  lon: number;
};

export type DomainsSelectItem = {
  id: number;
  value: string;
};

export type DomainSelectItem = {
  id: number;
  value: string;
};

const stringField = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

const normalizeDomainSelectItem = (raw: unknown): DomainSelectItem | null => {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const idRaw = r.id ?? r.pk;
  const id =
    typeof idRaw === 'number' && Number.isFinite(idRaw)
      ? idRaw
      : typeof idRaw === 'string'
        ? Number.parseInt(idRaw, 10)
        : NaN;
  if (!Number.isFinite(id)) return null;
  const label =
    stringField(r.value) ?? stringField(r.title) ?? stringField(r.name) ?? `Domain ${String(id)}`;
  return { id, value: label };
};

const normalizeDomainsList = (payload: unknown): DomainSelectItem[] => {
  const rows = Array.isArray(payload) ? payload : [];
  return rows.map(normalizeDomainSelectItem).filter((x): x is DomainSelectItem => x != null);
};

export type Domain = {
  pk: number;
  title: string;
  is_active?: boolean;
  meta?: Record<string, unknown>;
  website?: string;
  have_contract?: boolean;
  low?: number;
  high?: number;
  drop?: number;
  green_amount?: number;
  green_length?: number;
  blue_amount?: number;
  blue_length?: number;
  red_amount?: number;
  red_length?: number;
  black_amount?: number;
  black_length?: number;
  all_amount?: number;
  all_length?: number;
  lift_amount?: number;
  chair_amount?: number;
  gondola_amount?: number;
  funicular_amount?: number;
  slift_amount?: number;
  image?: string | null;
  resorts?: Array<{ id: number; title: string }>;
  resorts_count?: number;
  countries?: Array<{ id: number; title: string; code: string }>;
  restrictions?: Array<{
    type: string;
    name: string;
    from?: number | string | null;
    to?: number | string | null;
    date_from?: string | null;
    date_to?: string | null;
  }>;
  seasonal_periods?: Array<{
    season_type?: string;
    date_from: string;
    date_to: string;
  }>;
}

/** True when inventory marks the domain as inactive (supports alternate API casing). */
export function isDomainInactive(domain: Domain): boolean {
  if (domain.is_active === false) return true;
  const raw = domain as Domain & { IsActive?: boolean };
  if (raw.IsActive === false) return true;
  return false;
}

function normalizeDomainPayload(raw: unknown): Domain {
  const unwrapped =
    raw !== null &&
    typeof raw === 'object' &&
    'data' in raw &&
    (raw as { data: unknown }).data !== null &&
    typeof (raw as { data: unknown }).data === 'object'
      ? (raw as { data: unknown }).data
      : raw;

  if (unwrapped === null || typeof unwrapped !== 'object') {
    return unwrapped as Domain;
  }

  const o = unwrapped as Record<string, unknown>;
  const mergedActive =
    typeof o.is_active === 'boolean'
      ? o.is_active
      : typeof o.IsActive === 'boolean'
        ? o.IsActive
        : undefined;

  return { ...(o as unknown as Domain), is_active: mergedActive };
}

type CalculateOffersPersonWire = {
  start_date: string;
  days: number;
  birth_date?: string | null;
  age?: number | null;
  domain_id?: number;
};

type CalculateOffersRequestWire = {
  people: CalculateOffersPersonWire[];
  options?: CalculateOffersOptions;
};

function toCalculateOffersRequestWire(body: CalculateOffersRequest): CalculateOffersRequestWire {
  return {
    people: body.people.map((person) => ({
      start_date: person.startDate,
      days: person.days,
      birth_date: person.birthDate,
      age: person.age,
      domain_id: person.domain_id,
    })),
    options: body.options,
  };
}

class InventoryApiService {
  async getResorts(): Promise<Resort[]> {
    return api.get<Resort[]>('/api/inventory/resorts');
  }

  async searchResorts(geonameId?: number): Promise<Resort[]> {
    const params = geonameId ? `?geoname_id=${String(geonameId)}` : '';
    return api.get<Resort[]>(`/api/inventory/resorts/search${params}`);
  }

  async getDomains(): Promise<DomainSelectItem[]> {
    const raw = await api.get<unknown>('/api/inventory/domains');
    return normalizeDomainsList(raw);
  }

  async getDomainById(domainPk: number, includeInactive = false): Promise<Domain> {
    const params = includeInactive ? '?include_inactive=true' : '';
    const raw = await api.get<unknown>(`/api/inventory/domains/${String(domainPk)}${params}`);
    return normalizeDomainPayload(raw);
  }

  async calculateOffers(
    domainId: number,
    body: CalculateOffersRequest
  ): Promise<CalculateOffersResponse> {
    //console.log('Call deprecated calculateOffers with domainId in path. Consider using calculateOffersBatch instead.'); // eslint-disable-line no-console
    const raw = await api.post<CalculateOffersResponse | { data: CalculateOffersResponse }>(
      `/api/inventory/domains/${String(domainId)}/offers/calculate`,
      toCalculateOffersRequestWire(body)
    );
    return 'data' in raw ? raw.data : raw;
  }

  async calculateOffersBatch(body: CalculateOffersRequest): Promise<CalculateOffersResponse> {
    const raw = await api.post<CalculateOffersResponse | { data: CalculateOffersResponse }>(
      '/api/inventory/offers/calculate',
      toCalculateOffersRequestWire(body)
    );
    return 'data' in raw ? raw.data : raw;
  }
}

export type CalculateOffersPerson = {
  startDate: string;
  days: number;
  birthDate?: string | null;
  age?: number | null;
  domain_id?: number;
};

export type CalculateOffersOptions = {
  partial_failures_allowed?: boolean;
  final_confirmation?: boolean;
  domain_ids?: number[];
};

export type CalculateOffersRequest = {
  people: CalculateOffersPerson[];
  options?: CalculateOffersOptions;
};

export type OfferItem = {
  pass_pk?: number;
  title: string;
  period_title: string;
  customer_type: string;
  quantity: number;
  price: number;
  type: string;
  domain_id?: number;
};

export type CalculateOffersPersonResult = {
  startDate: string;
  days: number;
  birthDate?: string | null;
  items: OfferItem[];
  error?: string | null;
  recommendations?: unknown;
  domain_id?: number;
};

export type CalculateOffersResponse = {
  items: OfferItem[];
  total: number;
  currency: string;
  people?: CalculateOffersPersonResult[];
  /** Domain hero image URL when returned by calculate offers */
  image?: string | null;
};

export const inventoryApi = new InventoryApiService();
